import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import snapshot from '../data/pricing-snapshot.json';

const BOOT_LINES = [
  "[boot] 0v.network gateway · v3.14.2",
  "[boot] loading edge nodes ... ok  [hk · sg · sf · fra · nrt]",
  `[boot] handshake upstream pools  ... ok  [${(snapshot.vendors || []).length} vendors · ${(snapshot.data || []).length} models]`,
  "[boot] tls cert  *.0v.network    ... valid",
  "[boot] ready.",
];

// 日志为演示用假数据，但模型名与当前真实支持的模型保持一致
const LOG_LINES = [
  { t: "OK",    txt: "POST /v1/chat/completions   → claude-sonnet-4-6  · 142ms · 1,284 tok" },
  { t: "OK",    txt: "POST /v1/messages           → claude-opus-4-8    · 318ms · 4,201 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → gpt-5.5            · 198ms · 2,016 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → gpt-5.3-codex      · 264ms · 8,772 tok" },
  { t: "OK",    txt: "POST /v1/messages           → claude-opus-4-7    · 411ms · 3,092 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → claude-haiku-4-5   ·  87ms ·   612 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → gpt-5.4-mini       · 121ms ·   942 tok" },
  { t: "INFO",  txt: "route hit  edge=hk   upstream=anthropic          · cache=miss" },
  { t: "OK",    txt: "POST /v1/messages           → claude-sonnet-4-6  · 156ms · 2,407 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → gpt-5.4            · 174ms · 1,855 tok" },
  { t: "INFO",  txt: "route hit  edge=sg   upstream=openai             · cache=hit" },
  { t: "OK",    txt: "POST /v1/images/generations → gpt-image-2        · 832ms ·  1 img" },
  { t: "OK",    txt: "POST /v1/messages           → claude-opus-4-6    · 232ms · 1,104 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → claude-haiku-4-5   · 287ms · 5,621 tok" },
  { t: "OK",    txt: "POST /v1/chat/completions   → gpt-5.3-codex      · 612ms · 1,207 tok" },
];

function StatusDot({ kind = "ok" }) {
  return <span className={`dot dot-${kind}`} aria-hidden="true" />;
}

function Hero({ showCursor }) {
  const { t } = useLanguage();
  const [bootIdx, setBootIdx] = useState(0);
  const [logs, setLogs] = useState([]);
  const logRef = useRef(null);
  const lineCounter = useRef(0);

  useEffect(() => {
    if (bootIdx < BOOT_LINES.length) {
      const id = setTimeout(() => setBootIdx(i => i + 1), 360);
      return () => clearTimeout(id);
    }
  }, [bootIdx]);

  useEffect(() => {
    if (bootIdx < BOOT_LINES.length) return;
    const id = setInterval(() => {
      const next = LOG_LINES[lineCounter.current % LOG_LINES.length];
      lineCounter.current += 1;
      setLogs(prev => {
        const ts = new Date();
        const hh = String(ts.getUTCHours()).padStart(2, "0");
        const mm = String(ts.getUTCMinutes()).padStart(2, "0");
        const ss = String(ts.getUTCSeconds()).padStart(2, "0");
        const ms = String(ts.getUTCMilliseconds()).padStart(3, "0");
        const stamp = `${hh}:${mm}:${ss}.${ms}`;
        return [...prev.slice(-9), { ...next, stamp, key: lineCounter.current }];
      });
    }, 850);
    return () => clearInterval(id);
  }, [bootIdx]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  return (
    <section className="hero">
      <div className="hero-grid">
        <div className="hero-copy">
          <div className="hero-eyebrow">
            <span className="hb-bracket">[</span>
            <span>0v.network</span>
            <span className="hb-bracket">]</span>
            <span className="hb-sep">·</span>
            <span>{t('hero.eyebrow')}</span>
          </div>
          <h1 className="hero-title">
            {t('hero.title.line1')}<br />
            <span className="accent">{t('hero.title.line2')}</span>
          </h1>
          <p className="hero-lead">
            {t('hero.lead')}
            <span className="lead-mute"> {t('hero.lead.mute')}</span>
          </p>
          <div className="hero-cta">
            <a className="btn btn-primary" href="https://app.0v.network/dashboard/overview" target="_blank" rel="noreferrer">
              <span className="btn-glyph">▶</span> {t('hero.cta.console')}
              <span className="btn-meta">app.0v.network</span>
            </a>
            <a className="btn btn-ghost" href="#models">
              {t('hero.cta.models')} <span className="arr">↓</span>
            </a>
          </div>
          <div className="hero-meta">
            <div><span className="hm-k">endpoint</span><span className="hm-v">https://app.0v.network</span></div>
            <div><span className="hm-k">protocol</span><span className="hm-v">openai · anthropic</span></div>
          </div>
        </div>

        <div className="hero-term" role="img" aria-label={t('hero.term.label')}>
          <div className="term-chrome">
            <span className="tc-dot tc-r" /><span className="tc-dot tc-y" /><span className="tc-dot tc-g" />
            <span className="tc-title">~/0v ⟶ tail -f gateway.log</span>
            <span className="tc-live"><StatusDot /> LIVE</span>
          </div>
          <div className="term-body" ref={logRef}>
            {BOOT_LINES.slice(0, bootIdx).map((l, i) => (
              <div key={"b"+i} className="log-row log-boot">
                <span className="log-stamp">--:--:--</span>
                <span className="log-tag tag-info">BOOT</span>
                <span className="log-msg">{l.replace(/^\[boot\]\s*/, "")}</span>
              </div>
            ))}
            {bootIdx >= BOOT_LINES.length && logs.map(l => (
              <div key={l.key} className="log-row log-in">
                <span className="log-stamp">{l.stamp}</span>
                <span className={`log-tag tag-${l.t.toLowerCase()}`}>{l.t.padEnd(4, " ")}</span>
                <span className="log-msg">{l.txt}</span>
              </div>
            ))}
            <div className="log-row log-prompt">
              <span className="log-prompt-sym">$</span>
              <span className="log-msg">_{showCursor && <span className="caret" />}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
